import { model as router } from '!/router'
import { epgChannelsModel } from '$/epg/channels'
import { SEGMENT } from '@/constants'
import { type IUrlParams } from '@/types'
import { type LiveChannel } from '@setplex/tria-api'
import {
  combine,
  createEvent,
  createStore,
  restore,
  sample,
  type Store,
} from 'effector'
import { CategoryId } from '~/entities/epg/categories'
import { model as session } from '~/entities/session'
import { loadEpgCategoriesModel } from '~/features/epg/categories/load'
import { selectEpgCategoryModel } from '~/features/epg/categories/select'
import { loadEpgChannelsModel } from '~/features/epg/channels/load'
import { loadMoreEpgChannelsModel } from '~/features/epg/channels/load-more'
import { selectEpgChannelModel } from '~/features/epg/channels/select'
import { getRegionIdFromSegment } from '~/features/epg/regions/select'

// load initial epg info
export const init = createEvent<IUrlParams>()

export const $initData = createStore<{
  regionId: number | null
} | null>(null).on(init, (_, params) => ({
  regionId: getRegionIdFromSegment(params[SEGMENT.REGION]) ?? null,
}))

// reset widget on sign out
export const reset = createEvent()

// load more channels
export const more = createEvent()

// try to select channel
export const trySelect = createEvent<LiveChannel>()

// currently selected channel
// (it could be channel NOT in active channel list)
export const $selected = selectEpgChannelModel.$active

// currently active category channels
export const $activeList: Store<LiveChannel[]> = combine(
  selectEpgCategoryModel.$activeId,
  epgChannelsModel.$byCategory,
  (categoryId: number | CategoryId | null, [channels]) => {
    if (categoryId == null) return []
    return channels.get(categoryId) ?? []
  }
)

const $isAnyFavoriteChannelsLoaded = epgChannelsModel.$byCategory.map(
  ([channelsMap]) => {
    const channels = channelsMap.get(CategoryId.Favorite)
    return Boolean(channels?.length)
  }
)
// I use router state to navigate from other pages to the EPG page with preselecting first channel in Favorite category
// State is convenient because it is reset immediately after the URL has changed (for example, the first channel in the favorites category is set)
// and will not affect the selections of the first channels in other categories.
// Pass state with Favorite category id: { categoryId: CategoryId.Favorite }.

// Cases:
// - when some non-first channel from the favorites on the board has already been set before navigation.
// - when the epg board has not been visited before and the data has not yet been loaded (the first favorite channel will be set even if the request for favorites is executed slowly)
sample({
  clock: [router.$state, $isAnyFavoriteChannelsLoaded],
  source: {
    routerState: router.$state as Store<{ categoryId?: number | CategoryId }>,
    channels: epgChannelsModel.$byCategory,
  },
  filter: ({ routerState, channels: [channelsMap] }) => {
    const channels = channelsMap.get(CategoryId.Favorite)

    return (
      routerState?.categoryId === CategoryId.Favorite &&
      Boolean(channels?.length)
    )
  },
  fn: ({ channels: [channelsMap] }) => {
    const channels = channelsMap.get(CategoryId.Favorite)

    return channels?.[0].id || null
  },
  target: selectEpgChannelModel.setActiveId,
})

export const reloadFavoritesChannels = createEvent()

// on every category change request to load initial set of channels, if needed
sample({
  clock: [
    init,
    selectEpgCategoryModel.$activeId,
    loadEpgCategoriesModel.$ready,
    reloadFavoritesChannels,
  ],
  source: {
    initData: $initData,
    categoryId: selectEpgCategoryModel.$activeId,
    ready: loadEpgCategoriesModel.$ready,
    isAuthenticated: session.$isAuthenticated,
  },
  filter: ({ categoryId, ready, initData, isAuthenticated }) => {
    // if user is not authenticated and category is favorite request should be skipped
    if (categoryId === CategoryId.Favorite && !isAuthenticated) return false

    const regionId = initData?.regionId
    return categoryId != null && ready && regionId != null
  },
  fn: ({ categoryId, initData }) => ({
    categoryId,
    regionId: initData?.regionId,
  }),
  target: loadEpgChannelsModel.load,
})

// load more channels for active category
sample({
  clock: more,
  source: {
    initData: $initData,
    categoryId: selectEpgCategoryModel.$activeId,
  },
  filter: ({ categoryId, initData }) =>
    categoryId != null && Boolean(initData?.regionId),
  fn: ({ categoryId, initData }) => ({
    categoryId,
    regionId: initData?.regionId,
  }),
  target: loadMoreEpgChannelsModel.load,
})

export const setShouldNotAutoSetActiveId = createEvent<boolean>()
const $shouldNotAutoSetActiveId = createStore(false).on(
  setShouldNotAutoSetActiveId,
  (_, shouldNotAutoSetActiveId) => shouldNotAutoSetActiveId
)

// auto select first or url-chosen channel (auto select first - if there is NO channel in url)
sample({
  clock: [
    init,
    loadEpgChannelsModel.loadInitialChannelsFx.finally,
    $activeList,
  ],
  source: {
    init: restore(init, null).reset(reset),
    channels: $activeList,
    all: epgChannelsModel.$all,
    shouldNotAutoSetActiveId: $shouldNotAutoSetActiveId,
  },
  filter: ({
    init,
    channels,
    all: [allChannelsMap],
    shouldNotAutoSetActiveId,
  }) => {
    if (shouldNotAutoSetActiveId) {
      return false
    }
    return (init != null && channels.length > 0) || allChannelsMap.size > 0
  },
  fn: ({ init, channels }) => {
    return { channels, [SEGMENT.CHANNEL]: init?.[SEGMENT.CHANNEL] }
  },
  target: selectEpgChannelModel.autoSetActiveId,
})

// Select the channel
sample({
  clock: trySelect,
  fn: (channel) => {
    return channel.id
  },
  target: selectEpgChannelModel.setActiveId,
})

// need to create new map, instead of reinit, because this will leave old map in place
sample({
  clock: reset,
  fn: () => [new Map()] as const,
  target: epgChannelsModel.$all,
})

// need to create new maps, instead of reinit, because this will leave old maps in place
sample({
  clock: reset,
  fn: () => [new Map(), new Map()] as const,
  target: epgChannelsModel.$byCategory,
})

// need to create new map, instead of reinit, because this will leave old map in place
sample({
  clock: reset,
  fn: () => [new Map()] as const,
  target: loadEpgChannelsModel.$pending,
})
